/**
 * Manual Map Page (Iteration 12 — N7)
 * 
 * Full-width MapView with markers from selected project's locations.
 * For manual-only customers: shows marker map.
 * For hybrid customers: shows existing telemetry map with scoreboard added (LAT-20).
 * 
 * Route: /manual/map
 * 
 * LAT-19: map renders markers for all locations
 * LAT-20: hybrid sees telemetry map, not marker map
 * LAT-21: zero-count markers render with neutral tone
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel, IconButton, Tooltip } from '@mui/material';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';

import PageBody from '../elements/PageBody';
import PageTitle from '../elements/PageTitle';
import MapView from '../elements/MapView';
import ViewportScoreboard from '../elements/ViewportScoreboard';

import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { fetchScopes } from '../../store/slices/scopeSlice';
import { fetchCountsByProject } from '../../store/slices/countSlice';
import { useCapability } from '../../hooks/useCapability';
import { fetchProjectsByCustomer } from '../../store/slices/projectSlice';
// (fixtures now consumed inside the thunk / API client per R-07)

const ManualMapPage = () => {
  const dispatch = useAppDispatch();
  const capability = useCapability();
  const activeCustomer = useAppSelector((state) => state.customer.activeCustomer);
  const projectList = useAppSelector((state) => state.project.projectList) || [];

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // R-07b: read scopes and counts from the store instead of calling fixtures.
  const scopesForProject = useAppSelector(
    (state) => (selectedProjectId ? state.scope.scopesByProject[selectedProjectId] : null),
  );
  const countsForProject = useAppSelector(
    (state) => (selectedProjectId ? state.count.countsByProject[selectedProjectId] : null),
  );
  const [viewportBounds, setViewportBounds] = useState(null);
  // ASS-69 defect 2: persist collapse state across reloads (N8 requirement).
  const [scoreboardOpen, setScoreboardOpen] = useState(() => {
    try {
      return localStorage.getItem('assetrac-scoreboard-open') === 'true';
    } catch {
      return false;
    }
  });
  const [scoreboardUpdating, setScoreboardUpdating] = useState(false);

  const toggleScoreboard = useCallback((val) => {
    const next = typeof val === 'boolean' ? val : !scoreboardOpen;
    setScoreboardOpen(next);
    try { localStorage.setItem('assetrac-scoreboard-open', String(next)); } catch { /* non-fatal */ }
  }, [scoreboardOpen]);

  // Load projects via Redux
  useEffect(() => {
    if (!activeCustomer?.id) return;
    dispatch(fetchProjectsByCustomer(activeCustomer.id));
  }, [dispatch, activeCustomer?.id]);

  // Derive manual/hybrid projects from Redux store
  useEffect(() => {
    const manualProjects = projectList.filter(
      (p) => p.trackingMode === 'manual' || p.trackingMode === 'hybrid'
    );
    setProjects(manualProjects);

    // Auto-select first project
    if (manualProjects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(manualProjects[0].id);
    }
  }, [projectList]);

  // R-07b: dispatch scope+count thunks when the selected project changes.
  useEffect(() => {
    if (!selectedProjectId) {
      setMarkers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    dispatch(fetchScopes(selectedProjectId));
    dispatch(fetchCountsByProject(selectedProjectId));
  }, [selectedProjectId, dispatch]);

  // R-07b: recompute markers from store data whenever scopes or counts arrive.
  useEffect(() => {
    if (!selectedProjectId) return;
    if (!scopesForProject || !countsForProject) return;

    try {
      // Build a counts lookup by locationId
      const countsMap = {};
      for (const count of countsForProject) {
        countsMap[count.locationId] = count;
      }

      // Build markers from all locations across all scopes
      const allMarkers = [];
      for (const scope of scopesForProject) {
        for (const loc of scope.locations || []) {
          const counts = countsMap[loc.id] || {};
          const supplyManual = counts.supplyManual || 0;
          const demandManual = counts.demandManual || 0;
          const hasCounts = supplyManual > 0 || demandManual > 0;

          allMarkers.push({
            id: loc.id,
            lat: loc.lat,
            lng: loc.lng,
            label: loc.name,
            scopeName: scope.name,
            scopeId: scope.id,
            status: hasCounts ? 'has-counts' : 'no-counts', // LAT-21
            supplyManual,
            demandManual,
            supplyBeacon: counts.supplyBeacon || 0,
            demandBeacon: counts.demandBeacon || 0,
          });
        }
      }

      setMarkers(allMarkers);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to load locations');
      setLoading(false);
    }
  }, [selectedProjectId, scopesForProject, countsForProject]);

  // ASS-69 defect 1: filter markers to the current viewport bounds so the
  // scoreboard reflects what's visible on the map, not the full project.
  // viewportBounds is { north, south, east, west } set by handleViewportChange.
  const viewportMarkers = useMemo(() => {
    if (!viewportBounds || markers.length === 0) return markers;
    return markers.filter((m) => {
      const lat = m.lat ?? 0;
      const lng = m.lng ?? 0;
      return (
        lat >= viewportBounds.south &&
        lat <= viewportBounds.north &&
        lng >= viewportBounds.west &&
        lng <= viewportBounds.east
      );
    });
  }, [markers, viewportBounds]);

  const handleViewportChange = useCallback((bounds) => {
    setViewportBounds(bounds);
    setScoreboardUpdating(true);
    // ASS-69 follow-up: this is a SIMULATED delay, not a real
    // recomputation wait. The viewportMarkers useMemo recomputes synchronously.
    // The 200ms timeout exists only to flash the 'Updating…' indicator so the
    // user sees the scoreboard respond to viewport changes. If the recomputation
    // ever becomes async (e.g. web-worker spatial index), replace this with a
    // real loading signal.
    setTimeout(() => setScoreboardUpdating(false), 200);
  }, []);

  // Compute initial center from markers
  const initialCenter = markers.length > 0
    ? {
        lat: markers.reduce((sum, m) => sum + (m.lat || 0), 0) / markers.length,
        lng: markers.reduce((sum, m) => sum + (m.lng || 0), 0) / markers.length,
      }
    : { lat: 46.8, lng: -92.1 };

  return (
    <PageBody>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, pt: 2, pb: 1 }}>
        <PageTitle title="Manual Map" />

        {/* Project selector */}
        {projects.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Project</InputLabel>
            <Select
              value={selectedProjectId}
              label="Project"
              onChange={(e) => setSelectedProjectId(e.target.value)}
            >
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Scoreboard toggle */}
        <Tooltip title={scoreboardOpen ? 'Hide scoreboard' : 'Show scoreboard'}>
          <IconButton
            onClick={() => toggleScoreboard()}
            color={scoreboardOpen ? 'primary' : 'default'}
          >
            <LeaderboardIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Map */}
      <Box sx={{ flex: 1, px: 3, pb: 3, height: 'calc(100vh - 180px)', display: 'flex' }}>
        <Box sx={{ flex: 1 }}>
          <MapView
            markers={markers}
            onViewportChange={handleViewportChange}
            initialCenter={initialCenter}
            initialZoom={markers.length > 0 ? 14 : 10}
            loading={loading}
            error={error}
            emptyMessage="No locations defined yet"
            sx={{ borderRadius: 2, overflow: 'hidden', height: '100%' }}
          />
        </Box>

        {/* Viewport Scoreboard */}
        <ViewportScoreboard
          markers={viewportMarkers}
          loading={loading}
          error={error}
          supplyMode={capability.supplyMode}
          open={scoreboardOpen}
          onClose={() => toggleScoreboard(false)}
          updating={scoreboardUpdating}
        />
      </Box>
    </PageBody>
  );
};

export default ManualMapPage;
